/**
 * HAW (Human-Agent Ways of Working) modal — opened from `HawPanel`'s
 * click handler via the shared `useWorkshopModals` store.
 *
 * Ported from `_legacy/wd.html` lines 826-869 (`#hawModal`):
 *   - `.modal-shell` chrome with `width:88vw; max-width:1080px` and
 *     legacy `.modal-header` / `.modal-body` / `.modal-footer` classes
 *     (same convention `ReportModal` uses).
 *   - Two-column body: 380 px diagram on the left, scrollable mode
 *     cards on the right.
 *
 * The left column re-renders the same HAW bubble SVG that `HawPanel`
 * draws — re-using the geometry constants + `HAW_MODES` data so the
 * diagram stays in lockstep with the panel without extracting a
 * shared component (kept lean to avoid touching `HawPanel`'s existing
 * tooltip wiring).
 *
 * Right column shows an intro callout + a card per outer mode (TL /
 * TR / BL / BR — centre is the destination state, not a mode). Each
 * card uses the mode's `accent` for the badge text + the card's
 * left-border accent. Copy comes verbatim from `HAW_MODES` in
 * `./haw-content.ts`, which itself was ported from
 * `_legacy/xaes-content.js` lines 26-70.
 */

import { useMemo, type CSSProperties } from "react";
import { Users, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { resolveHawModes } from "@/features/workshop/resolve-workshop-diagram";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { HAW_MODES, type HawMode, type HawModeId } from "./haw-content";

type HawModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/* Geometry constants — kept in sync with `HawPanel.tsx`. */
const VIEW_W = 480;
const VIEW_H = 320;
const CENTRE = { cx: 240, cy: 165 };
const OUTER_R = 72;
const OUTER_RING_R = 62;
const CENTRE_R = 80;
const CENTRE_RING_R = 70;

const BUBBLE_POSITIONS: Record<Exclude<HawModeId, "centre">, { cx: number; cy: number }> = {
  tl: { cx: 108, cy: 87 },
  tr: { cx: 372, cy: 87 },
  bl: { cx: 108, cy: 243 },
  br: { cx: 372, cy: 243 },
};

/**
 * Mirror of `HawPanel`'s ring-speed custom-property override. The
 * default `.bubble-dash-ring` spin is 110s; vanilla SVG used 70s.
 * CSS custom properties aren't part of the `CSSProperties` type, so
 * we cast through `Record<string, string>`.
 */
const RING_SPEED_STYLE = {
  "--bubble-ring-speed": "70s",
} as unknown as CSSProperties;

const INTRO_BODY =
  "The Human-Agent Ways of Working model defines four operating modes for how humans and AI agents collaborate. Each workflow can be matched to the mode that best fits its risk profile, regulatory needs, and the level of autonomy required.";

function splitTitle(title: string): [string, string] {
  const trimmed = title.toUpperCase();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return [trimmed, ""];
  return [trimmed.slice(0, lastSpace), trimmed.slice(lastSpace + 1)];
}

/**
 * Static (non-interactive) HAW bubble diagram for the modal. Same
 * geometry as `HawPanel` but without hover tooltips — the cards on
 * the right already carry the long-form descriptions.
 */
function HawDiagramStatic() {
  const outerModes = HAW_MODES.filter(
    (m): m is HawMode & { id: Exclude<HawModeId, "centre"> } => m.id !== "centre",
  );
  const centreMode = HAW_MODES.find((m) => m.id === "centre");

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", width: "100%", height: "100%" }}
      role="img"
      aria-label="Four HAW modes connected to a central Agentic Enterprise concept"
    >
      {outerModes.map((mode) => {
        const pos = BUBBLE_POSITIONS[mode.id];
        return (
          <line
            key={`con-${mode.id}`}
            x1={pos.cx}
            y1={pos.cy}
            x2={CENTRE.cx}
            y2={CENTRE.cy}
            stroke={mode.accent}
            strokeWidth={1}
            strokeDasharray="4,3"
            opacity={0.35}
          />
        );
      })}

      {outerModes.map((mode) => {
        const pos = BUBBLE_POSITIONS[mode.id];
        const tint = `color-mix(in srgb, ${mode.accent} 6%, transparent)`;
        const [line1, line2] = splitTitle(mode.title);
        return (
          <g key={mode.id}>
            <circle
              cx={pos.cx}
              cy={pos.cy}
              r={OUTER_R}
              fill={tint}
              stroke={mode.accent}
              strokeWidth={1.5}
              opacity={0.7}
            />
            <circle
              className="bubble-dash-ring"
              style={RING_SPEED_STYLE}
              cx={pos.cx}
              cy={pos.cy}
              r={OUTER_RING_R}
              fill="none"
              stroke={mode.accent}
              strokeWidth={1}
              strokeDasharray="3,4"
              opacity={0.4}
            />
            <text
              x={pos.cx}
              y={pos.cy + 2}
              textAnchor="middle"
              fill={mode.accent}
              fontFamily="Inter, sans-serif"
              fontWeight={700}
              fontSize={11}
              letterSpacing={1}
            >
              {line1}
            </text>
            {line2 && (
              <text
                x={pos.cx}
                y={pos.cy + 16}
                textAnchor="middle"
                fill={mode.accent}
                fontFamily="Inter, sans-serif"
                fontWeight={700}
                fontSize={11}
                letterSpacing={1}
              >
                {line2}
              </text>
            )}
          </g>
        );
      })}

      {centreMode && (
        <g>
          <circle
            cx={CENTRE.cx}
            cy={CENTRE.cy}
            r={CENTRE_R}
            fill={`color-mix(in srgb, ${centreMode.accent} 5%, transparent)`}
            stroke={centreMode.accent}
            strokeWidth={1.5}
          />
          <circle
            className="bubble-dash-ring"
            style={RING_SPEED_STYLE}
            cx={CENTRE.cx}
            cy={CENTRE.cy}
            r={CENTRE_RING_R}
            fill="none"
            stroke={centreMode.accent}
            strokeWidth={0.8}
            strokeDasharray="2,3"
            opacity={0.5}
          />
          <text
            x={CENTRE.cx}
            y={CENTRE.cy - 4}
            textAnchor="middle"
            fill={centreMode.accent}
            fontFamily="Inter, sans-serif"
            fontWeight={700}
            fontSize={12}
            letterSpacing={1}
          >
            AGENTIC
          </text>
          <text
            x={CENTRE.cx}
            y={CENTRE.cy + 12}
            textAnchor="middle"
            fill={centreMode.accent}
            fontFamily="Inter, sans-serif"
            fontWeight={700}
            fontSize={12}
            letterSpacing={1}
          >
            ENTERPRISE
          </text>
        </g>
      )}
    </svg>
  );
}

function ModeCard({ mode }: { mode: HawMode }) {
  return (
    <div
      style={{
        padding: "14px 16px",
        background: "var(--bg-panel-alt)",
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.1)",
        borderLeft: `3px solid ${mode.accent}`,
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
          flexWrap: "wrap",
        }}
      >
        {mode.badge && (
          <span
            style={{
              padding: "1px 8px",
              borderRadius: 10,
              fontSize: "var(--fs-label)",
              fontWeight: 700,
              letterSpacing: 1.5,
              color: mode.accent,
              background: `color-mix(in srgb, ${mode.accent} 12%, transparent)`,
              border: `1px solid ${mode.accent}`,
            }}
          >
            {mode.badge}
          </span>
        )}
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "var(--fs-title)",
            fontWeight: 700,
            color: mode.accent,
            letterSpacing: 0.5,
          }}
        >
          {mode.title}
        </span>
      </div>
      {mode.subtitle && (
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "var(--fs-label)",
            fontStyle: "italic",
            color: "var(--text-muted)",
            marginBottom: 8,
            letterSpacing: 0.3,
          }}
        >
          {mode.subtitle}
        </div>
      )}
      <div
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: "var(--fs-base)",
          color: "var(--text-secondary)",
          lineHeight: 1.7,
        }}
      >
        {mode.body}
      </div>
    </div>
  );
}

export function HawModal({ open, onOpenChange }: HawModalProps) {
  const override = useEffectiveWorkshopOverride("WS4");
  const modes = useMemo(() => resolveHawModes(override), [override]);
  const outerModes = modes.filter((m) => m.id !== "centre");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        // `duration-300` + `slide-in-from-bottom-4` give the HAW modal
        // a more pronounced entrance than the default fade+zoom alone.
        // Other modals (Report/Analysis) ride on the default 200 ms
        // fade+zoom from `PanelDialogContent`; HAW reuses that wrapper
        // and stacks the slide for legacy parity with how the vanilla
        // `#hawModal` shell felt when flipped from `display:none`.
        className="border-0 bg-transparent p-0 shadow-none duration-300 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-4"
        // Legacy backdrop (`_legacy/wd.html` line 827) was
        // `background: var(--bg-panel); backdrop-filter: blur(6px);`
        // — a near-opaque dark wash. The default `bg-black/80` reads
        // too pale next to that and pure black drops the legacy navy
        // tint, so reference `--bg-panel` directly (it already carries
        // its own 0.97 alpha in dark theme) and override the wrapper's
        // default 8 px blur down to legacy's 6 px. twMerge resolves
        // the conflicting `bg-*` and `backdrop-blur-*` against the
        // wrapper's `bg-black/80`, so the override wins.
        overlayClassName="bg-[var(--bg-panel)] backdrop-blur-[6px]"
        style={{ outline: "none", width: "88vw", maxWidth: 1080 }}
      >
        <DialogTitle className="sr-only">Human-Agent Ways of Working</DialogTitle>
        <DialogDescription className="sr-only">
          The four collaboration modes between humans and AI agents that
          make up the Human-Agent Ways of Working model.
        </DialogDescription>
        <div
          className="modal-shell"
          style={{
            width: "100%",
            maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
          }}
        >
          {/* HEADER */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Users
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-sky)" }}
              />
              <span className="modal-title">HUMAN-AGENT WAYS OF WORKING</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-sky-rgb), 0.1)",
                  border: "1px solid var(--accent-sky)",
                  borderRadius: 10,
                  fontSize: "var(--fs-label)",
                  fontWeight: 600,
                  letterSpacing: 1,
                  color: "var(--accent-sky)",
                }}
              >
                OPS MODEL
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--accent-green)",
                  boxShadow: "var(--glow-green)",
                  animation: "pulse 2s infinite",
                }}
              />
              <span
                style={{
                  fontSize: "var(--fs-label)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                Blueprint
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* BODY: diagram (left) + descriptions (right). */}
          <div
            style={{
              display: "flex",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {/* LEFT — HAW diagram clone. */}
            <div
              style={{
                width: 380,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                background:
                  "radial-gradient(ellipse at 40% 50%, var(--bg-deep) 0%, var(--bg-deep) 100%)",
                borderRight: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
              }}
            >
              <HawDiagramStatic />
            </div>

            {/* RIGHT — intro callout + mode cards. */}
            <div
              className="modal-body"
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 16,
                padding: 24,
                overflowY: "auto",
              }}
            >
              {/* Intro callout — accent border-left in sky, matching
                  the centre bubble's accent. */}
              <div
                style={{
                  padding: "12px 16px",
                  background: "var(--bg-panel-alt)",
                  border: "1px solid rgba(var(--accent-sky-rgb), 0.2)",
                  borderLeft: "3px solid var(--accent-sky)",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "var(--fs-base)",
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    color: "var(--accent-sky)",
                    marginBottom: 5,
                  }}
                >
                  About This Model
                </div>
                <div
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "var(--fs-base)",
                    color: "var(--text-secondary)",
                    lineHeight: 1.7,
                  }}
                >
                  {INTRO_BODY}
                </div>
              </div>

              {/* Mode cards. */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {outerModes.map((mode) => (
                  <ModeCard key={mode.id} mode={mode} />
                ))}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">
              WS4 &ndash; Human-Agent Ways of Working
            </span>
            <span className="modal-footer-right">4 COLLABORATION MODES</span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
